import { Grid } from "@mui/material";
import { FoodDataSource } from "@notemeal/graphql-types";
import { useRegion } from "apps/web/src/hooks/useRegion";
import React from "react";
import { Controller, UseFormReturn, useFormState } from "react-hook-form";
import { UnitWithConversionsFragment } from "../../../../types";
import { LocaleMultiSelect } from "../../../Locale/LocaleMultiSelect";
import { useSnackbar } from "../../../Snackbar/SnackbarContext";
import { BaseFoodFormType } from "../../FoodFormSchema/FoodFormSchema";
import { deselectUsdaFood, shouldDeselectUsdaFoodOnSourceChange } from "../../FoodFormSchema/usdaFoodUtils";
import { FoodDataSourceSelect } from "./FoodDataSourceSelect";
import { UsdaSearchBar } from "./UsdaSearchBar";

interface FoodDataSourceSectionProps {
  form: UseFormReturn<BaseFoodFormType>;
  setIsLoading: (isLoading: boolean) => void;
  unitsData: readonly UnitWithConversionsFragment[];
  gramUnit: UnitWithConversionsFragment;
  disabled?: boolean;
}

export const FoodDataSourceSection = ({ form, unitsData, gramUnit, setIsLoading, disabled }: FoodDataSourceSectionProps) => {
  const { watch, control } = form;
  const { errors } = useFormState({ control });
  const [watchedId, watchedMode, watchedSource] = watch(["id", "mode", "source"]);
  const { setMessage } = useSnackbar();
  const { isApacRegion } = useRegion();

  const isOrgMode = watchedMode === "org";
  const isStaffMode = watchedMode === "staff";

  const showUsda = !isApacRegion && (isOrgMode || watchedSource === FoodDataSource.usda);
  const showManual = isStaffMode && watchedSource === FoodDataSource.manual;

  return (
    <>
      {isStaffMode && (
        <Grid
          item
          container
          spacing={2}>
          <Grid
            item
            xs={6}
            sx={{ py: 1.5 }}>
            <Controller
              name={"source"}
              control={control}
              render={({ field: { ref, onChange, ...field } }) => (
                <FoodDataSourceSelect
                  source={field.value as FoodDataSource}
                  isEdit={!!watchedId}
                  error={Boolean(errors.source)}
                  helperText={errors.source?.message}
                  onChange={source => {
                    if (source === FoodDataSource.mccance) {
                      setMessage("error", "Cannot change existing food to McCance");
                      return;
                    }
                    if (source === FoodDataSource.ausnut) {
                      setMessage("error", "Cannot change existing food to AUSNUT");
                      return;
                    }
                    if (
                      shouldDeselectUsdaFoodOnSourceChange(
                        source as FoodDataSource,
                        watchedSource as FoodDataSource,
                        form.getValues("usdaFdcId")
                      )
                    ) {
                      deselectUsdaFood(form);
                      return;
                    }
                    onChange(source);
                  }}
                  disabled={disabled}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} sx={{ display: showManual ? "block" : "none" }}>
            <Controller
              name={"locales"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <LocaleMultiSelect
                  values={value || []}
                  error={Boolean(errors.locales)}
                  helperText={errors.locales?.message}
                  onChange={onChange}
                  sx={{ mt: 3 }}
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      {/* TODO add mccance */}

      {showUsda && (
        <Grid
          item
          xs={12}
          mb={1.5}>
          <UsdaSearchBar
            disabled={disabled}
            form={form}
            unitsData={unitsData}
            gramUnit={gramUnit}
            setIsLoading={setIsLoading} />
        </Grid>
      )}
    </>
  );
};
